import { useQuery } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { isAxiosError } from '@/react/portainer/services/axios/utils/isAxiosError';

import { PolicyId, TypedPolicy } from '../types';

import { queryKeys } from './query-keys';

export function usePolicy(
  id: PolicyId,
  options?: {
    refetchInterval?: number | false;
    enabled?: boolean;
    staleTime?: number;
  }
) {
  return useQuery({
    queryKey: queryKeys.detail(id),
    queryFn: () => getPolicy(id),
    // falsy policyIDs, including 0, disable the query
    enabled: !!id && (options?.enabled ?? true),
    refetchInterval: (policy) =>
      policy === null ? false : (options?.refetchInterval ?? false),
    staleTime: options?.staleTime,
    ...withError('Unable to load policy'),
  });
}

async function getPolicy(id: PolicyId): Promise<TypedPolicy<unknown> | null> {
  try {
    const { data } = await axios.get<TypedPolicy<unknown>>(`/policies/${id}`);
    return data;
  } catch (error) {
    // a missing policy is a state the caller renders, not a failure to notify about
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw parseAxiosError(error, 'Unable to retrieve policy');
  }
}
